/**
 * KatrinaHome — Prototype 01a (State 1)
 * Route: /prototypes/katrina-home
 *
 * The revised opening of the Katrina experience.
 *
 * Katrina lands here on an ordinary morning. This is her regular Toto Home
 * — the same structural surface any COO would see. The demonstration has
 * not begun. Nothing is animated. Nothing is announced. Among the normal
 * items on her Home is a new message from Damon Mitchell.
 *
 * The message's importance comes from placement, restraint, and sender
 * — not from color, animation, or urgency. It is visually the same kind
 * of card as the others. The COO must be able to imagine that this is
 * simply what a Wednesday morning looks like.
 *
 * Presence exists on this screen only as a small resting mark in the
 * corner — part of the product's regular language. It does not compete.
 * Law #1 requires Presence never to be created, so its presence here
 * (small and unnoticed) is what allows it to *awaken* later without
 * being conjured.
 */
import React from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";
import { CheckCircle2, Clock, MessageSquare, Info } from "lucide-react";

const BOUTIQUE_COLOR = {
  PLT: "bg-emerald-500",
  FLL: "bg-sky-500",
  WYN: "bg-pink-500",
  PNC: "bg-orange-500",
};

export default function KatrinaHome() {
  const notes = {
    communicates:
      "This is Katrina's ordinary Home on an ordinary morning. It should feel like real software she uses every day. Among the normal items, a new message from Damon has arrived — noteworthy because of who sent it, not because the interface is treating it specially.",
    placeholder: [
      "The three non-message items are illustrative. Real items would be drawn from the actual COO workflow (financials, offers, boutique operations).",
      "The 'Received this morning' timestamp is static text, not live.",
      "The 'Open message' action is inert — clicking does nothing yet. The transition to State 2 will be added only once we're aligned on this screen.",
    ],
    decisions: [
      "What Katrina should notice first: the greeting, or the message card? Currently the greeting reads first, then the eye scans down through the items in order — Damon's message sits third of four, deliberately not at the top.",
      "Should the message card carry a colored dot for boutique (PLT), or none? Currently: PLT dot, to keep it visually consistent with other items.",
      "Should the sender appear as a name only, or with a small avatar? Currently: name only. Adding an avatar risks feeling like a chat app.",
      "Is 'New message' the right label, or does 'Message' alone suffice given the boldness treatment? Currently: 'New message' as a quiet eyebrow, since 'new' is why it stands out among the items.",
    ],
    assumptions: [
      "Katrina already knows Damon and would recognize his name without any explanation.",
      "The prototype does not gate by role. The URL is unauthenticated and shows the same Home to anyone opening it.",
      "The other three items are supporting fiction, chosen to make the message feel embedded in a real workday — not a stage built around it.",
      "Presence exists on this Home already, in a small resting form in the top-right corner. Not as a feature. As part of the room's furniture.",
    ],
  };

  return (
    <PrototypeShell title="Prototype 01 · Katrina Home (state 1 — ordinary morning)" notes={notes}>
      <section className="max-w-5xl mx-auto px-8 lg:px-12 pt-6 pb-16" data-testid="katrina-home">
        {/* Header — just a greeting. No demonstration cue. */}
        <header className="flex items-start justify-between mb-14">
          <div>
            <h1
              className="font-serif-display text-5xl text-stone-900 leading-[1.05]"
              style={{ fontWeight: 400 }}
              data-testid="home-greeting"
            >
              Good morning, Katrina.
            </h1>
            <p className="text-sm text-stone-500 mt-3">Wednesday, February 21.</p>
          </div>
          {/* Presence at rest — small, unobtrusive. Part of the product language, not a feature. */}
          <PresenceForm size={44} tone="neutral" testId="home-presence-rest" />
        </header>

        {/* Home items — realistic COO surface. Same visual card treatment across all. */}
        <div className="space-y-3" data-testid="home-items">
          {/* Item 1 — one item ready for review */}
          <HomeCard
            testid="item-financials"
            boutiqueCode="PLT"
            eyebrow="Awaiting your review"
            title="January boutique financials"
            subline="Compiled Monday. All four boutiques closed the month on plan."
            meta="Review by Fri"
            icon={<CheckCircle2 size={13} className="text-emerald-600" />}
            action="Review"
          />

          {/* Item 2 — one requiring awareness */}
          <HomeCard
            testid="item-awareness"
            boutiqueCode="FLL"
            eyebrow="For your awareness"
            title="Sheila hired a new Experience Guide for Fort Lauderdale"
            subline="Rosa Álvarez starts Monday. No decision needed."
            meta="Yesterday"
            icon={<Info size={13} className="text-stone-400" />}
            action="View details"
          />

          {/* Item 3 — Damon's message. Restrained. Same card treatment. */}
          <HomeCard
            testid="item-damon-message"
            boutiqueCode="PLT"
            eyebrow="New message"
            title="Damon Mitchell"
            subline="I'd like to show you something."
            meta="Received this morning"
            icon={<MessageSquare size={13} className="text-stone-500" />}
            action="Open message"
            actionEmphasized
          />

          {/* Item 4 — one quiet operational update */}
          <HomeCard
            testid="item-renewals"
            boutiqueCode="WYN"
            eyebrow="Operations"
            title="March scheduling underway"
            subline="Caio is resolving two therapist availability conflicts for the first week of March."
            meta="Ongoing"
            icon={<Clock size={13} className="text-stone-400" />}
            action="View"
          />
        </div>

        {/* Bottom legend — matches other prototypes for continuity of language */}
        <div className="mt-24 flex items-center justify-center gap-6 text-[10px] uppercase tracking-[0.25em] text-stone-400">
          <LegendDot code="PLT" label="Plantation" />
          <LegendDot code="FLL" label="Fort Lauderdale" />
          <LegendDot code="WYN" label="Wynwood" />
          <LegendDot code="PNC" label="Pinecrest" />
        </div>
      </section>
    </PrototypeShell>
  );
}

function HomeCard({ boutiqueCode, eyebrow, title, subline, meta, icon, action, actionEmphasized, testid }) {
  return (
    <article
      className="rounded-2xl bg-white subtle-border px-6 py-5 hover:shadow-sm transition-shadow"
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3 min-w-0 flex-1">
          <span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${BOUTIQUE_COLOR[boutiqueCode]}`} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400">{eyebrow}</div>
            <h2 className="text-[15px] text-stone-900 mt-0.5 truncate" style={{ fontWeight: 500 }}>{title}</h2>
            <p className="text-[13px] text-stone-600 mt-1 leading-relaxed">{subline}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[11px] text-stone-400">{meta}</span>
          <button
            data-testid={`${testid}-action`}
            className={
              actionEmphasized
                ? "text-[12px] text-stone-900 hover:text-black underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900"
                : "text-[12px] text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-transparent hover:decoration-stone-300"
            }
          >
            {action}
          </button>
        </div>
      </div>
    </article>
  );
}

function LegendDot({ code, label }) {
  const dot = { PLT: "bg-emerald-500", FLL: "bg-sky-500", WYN: "bg-pink-500", PNC: "bg-orange-500" }[code];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}
